import { format, parseISO } from 'date-fns'

// Helper function to parse dates consistently
const parseDate = (date: Date | string): Date => {
  return typeof date === 'string' ? parseISO(date) : date
}

// Helper function to create a day range
const createDayRange = (date: Date): { start: Date, end: Date } => {
  const startOfDay = new Date(date.getFullYear(), date.getMonth(), date.getDate())
  const endOfDay = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59)
  return { start: startOfDay, end: endOfDay }
}

// Helper function to create a week range
const createWeekRange = (date: Date, weekOffset: number = 0): { start: Date, end: Date } => {
  const day = date.getDay()
  const diff = day === 0 ? -6 : 1 - day // Get to Monday of current week
  const startOfWeek = new Date(date)
  startOfWeek.setDate(date.getDate() + diff + (weekOffset * 7))
  startOfWeek.setHours(0, 0, 0, 0)
  
  const endOfWeek = new Date(startOfWeek)
  endOfWeek.setDate(startOfWeek.getDate() + 6) // Set to Sunday
  endOfWeek.setHours(23, 59, 59, 999)
  
  return { start: startOfWeek, end: endOfWeek }
}

// Helper function to format a date range
const formatDateRange = (start: Date, end: Date): { start: string, end: string } => {
  return {
    start: formatDateForInput(start),
    end: formatDateForInput(end)
  }
}

// Helper function to check if a date range matches a given range
const isDateRangeMatch = (startDate: string, endDate: string, targetRange: { start: Date, end: Date }): boolean => {
  const start = parseDate(startDate)
  const end = parseDate(endDate)
  const tStart = targetRange.start
  const tEnd = targetRange.end
  // Compare only up to minutes
  const sameStart =
    start.getFullYear() === tStart.getFullYear() &&
    start.getMonth() === tStart.getMonth() &&
    start.getDate() === tStart.getDate() &&
    start.getHours() === tStart.getHours() &&
    start.getMinutes() === tStart.getMinutes();
  const sameEnd =
    end.getFullYear() === tEnd.getFullYear() &&
    end.getMonth() === tEnd.getMonth() &&
    end.getDate() === tEnd.getDate() &&
    end.getHours() === tEnd.getHours() &&
    end.getMinutes() === tEnd.getMinutes();
  return sameStart && sameEnd;
}

/**
 * Converts a local date to UTC ISO string
 */
export const toUTCISO = (date: Date): string => {
  return new Date(
    Date.UTC(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      date.getHours(),
      date.getMinutes()
    )
  ).toISOString()
}

/**
 * Converts a UTC ISO string to local date
 */
export const fromUTCISO = (isoString: string): Date => {
  const date = parseISO(isoString)
  return new Date(date.getTime() + (date.getTimezoneOffset() * 60000))
}

/**
 * Checks if two dates are on the same day
 */
export const isSameDay = (date1: Date | string, date2: Date | string): boolean => {
  const d1 = parseDate(date1)
  const d2 = parseDate(date2)
  return d1.getFullYear() === d2.getFullYear() &&
         d1.getMonth() === d2.getMonth() &&
         d1.getDate() === d2.getDate()
}

/**
 * Formats a date for display in the UI
 * Shows only time if the date is within the same day as the reference date
 */
export const formatDateForDisplay = (date: Date | string, referenceDate?: Date | string): string => {
  const d = parseDate(date)
  if (referenceDate && isSameDay(d, referenceDate)) {
    return format(d, 'HH:mm')
  }
  return format(d, 'MMM d, HH:mm')
}

/**
 * Formats a date for datetime-local input
 */
export const formatDateForInput = (date: Date | string): string => {
  const d = parseDate(date)
  return format(d, "yyyy-MM-dd'T'HH:mm")
}

/**
 * Gets the current date in local timezone formatted for datetime-local input
 */
export const getCurrentLocalDate = (): string => {
  return formatDateForInput(new Date())
}

/**
 * Gets the start of the current day in UTC
 */
export const getStartOfDayUTC = (): string => {
  const now = new Date()
  const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  return toUTCISO(startOfDay)
}

/**
 * Gets the end of the current day in UTC
 */
export const getEndOfDayUTC = (): string => {
  const now = new Date()
  const endOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999)
  return toUTCISO(endOfDay)
}

/**
 * Converts a UTC ISO string to a local datetime-local input string
 * (e.g. 2025-05-29T14:00:00Z -> 2025-05-29T15:00 if user is UTC+1)
 */
export const utcToLocalInput = (isoString: string): string => {
  if (!isoString) return ''
  const date = new Date(isoString)
  const pad = (n: number) => n.toString().padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}

/**
 * Converts a local datetime-local input string to a UTC ISO string
 * (e.g. 2025-05-29T15:00 -> 2025-05-29T14:00:00Z if user is UTC+1)
 */
export const localInputToUTC = (inputString: string): string => {
  if (!inputString) return ''
  const date = new Date(inputString) // This is interpreted as local time
  return date.toISOString() // This is UTC
}

/**
 * Gets a human-readable label for a date range
 * Returns labels like "Today", "Yesterday", "This Week", "Last 7 Days", etc.
 */
export const getRangeLabel = (startDate: string, endDate: string): string => {
  const now = new Date();
  const start = parseDate(startDate);
  const end = parseDate(endDate);

  // Today
  const todayRange = createDayRange(now);
  if (isDateRangeMatch(startDate, endDate, todayRange)) return 'Today';

  // Yesterday
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  const yesterdayRange = createDayRange(yesterday);
  if (isDateRangeMatch(startDate, endDate, yesterdayRange)) return 'Yesterday';

  // Tomorrow
  const tomorrow = new Date(now);
  tomorrow.setDate(now.getDate() + 1);
  const tomorrowRange = createDayRange(tomorrow);
  if (isDateRangeMatch(startDate, endDate, tomorrowRange)) return 'Tomorrow';

  // This Week
  const thisWeekRange = createWeekRange(now);
  if (isDateRangeMatch(startDate, endDate, thisWeekRange)) return 'This Week';

  // Last Week
  const lastWeekRange = createWeekRange(now, -1);
  if (isDateRangeMatch(startDate, endDate, lastWeekRange)) return 'Last Week';

  // Next Week
  const nextWeekRange = createWeekRange(now, 1);
  if (isDateRangeMatch(startDate, endDate, nextWeekRange)) return 'Next Week';

  // Last 7/30/90 Days
  const diffTime = Math.abs(end.getTime() - start.getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  if (end.toDateString() === now.toDateString()) {
    if (diffDays === 7) return 'Last 7 Days';
    if (diffDays === 30) return 'Last 30 Days';
    if (diffDays === 90) return 'Last 90 Days';
  }

  // Otherwise, show formatted range
  // If time is not 00:00 or 23:59, show time, else just date
  const showTime = start.getHours() !== 0 || start.getMinutes() !== 0 || end.getHours() !== 23 || end.getMinutes() !== 59;
  const startStr = format(start, showTime ? 'MMM d, HH:mm' : 'MMM d');
  const endStr = format(end, showTime ? 'MMM d, HH:mm' : 'MMM d, yyyy');
  if (startStr === endStr) return startStr;
  return `${startStr} – ${endStr}`;
};

/**
 * Gets a human-readable duration string between two dates
 */
export const getDurationString = (startDate: string, endDate: string): string => {
  if (!startDate || !endDate) return '';

  const start = parseDate(startDate);
  const end = parseDate(endDate);
  const diffTime = Math.abs(end.getTime() - start.getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays === 1) return '1 day';
  if (diffDays < 7) return `${diffDays} days`;
  if (diffDays < 30) {
    const weeks = Math.floor(diffDays / 7);
    return `${weeks} week${weeks === 1 ? '' : 's'}`;
  }
  const months = Math.floor(diffDays / 30);
  return `${months} month${months === 1 ? '' : 's'}`;
}

/**
 * Gets the start and end dates for today
 */
export const getTodayRange = (): { start: string, end: string } => {
  const now = new Date()
  const range = createDayRange(now)
  return formatDateRange(range.start, range.end)
}

/**
 * Gets the start and end dates for yesterday
 */
export const getYesterdayRange = (): { start: string, end: string } => {
  const now = new Date()
  const yesterday = new Date(now)
  yesterday.setDate(now.getDate() - 1)
  const range = createDayRange(yesterday)
  return formatDateRange(range.start, range.end)
}

/**
 * Gets the start and end dates for tomorrow
 */
export const getTomorrowRange = (): { start: string, end: string } => {
  const now = new Date()
  const tomorrow = new Date(now)
  tomorrow.setDate(now.getDate() + 1)
  const range = createDayRange(tomorrow)
  return formatDateRange(range.start, range.end)
}

/**
 * Gets the start and end dates for this week (Monday to Sunday)
 */
export const getThisWeekRange = (): { start: string, end: string } => {
  const now = new Date()
  const range = createWeekRange(now)
  return formatDateRange(range.start, range.end)
}

/**
 * Gets the start and end dates for last week (Monday to Sunday)
 */
export const getLastWeekRange = (): { start: string, end: string } => {
  const now = new Date()
  const range = createWeekRange(now, -1)
  return formatDateRange(range.start, range.end)
}

/**
 * Gets the start and end dates for next week (Monday to Sunday)
 */
export const getNextWeekRange = (): { start: string, end: string } => {
  const now = new Date()
  const range = createWeekRange(now, 1)
  return formatDateRange(range.start, range.end)
}

/**
 * Gets the start and end dates for the last N days
 */
export const getLastNDaysRange = (days: number): { start: string, end: string } => {
  const now = new Date()
  const endDate = new Date(now)
  const startDate = new Date(now)
  startDate.setDate(now.getDate() - days)
  
  return {
    start: formatDateForInput(startDate),
    end: formatDateForInput(endDate)
  }
}

/**
 * Checks if a date range matches today
 */
export const isTodayRange = (startDate: string, endDate: string): boolean => {
  const now = new Date()
  const range = createDayRange(now)
  return isDateRangeMatch(startDate, endDate, range)
}

/**
 * Checks if a date range matches yesterday
 */
export const isYesterdayRange = (startDate: string, endDate: string): boolean => {
  const now = new Date()
  const yesterday = new Date(now)
  yesterday.setDate(now.getDate() - 1)
  const range = createDayRange(yesterday)
  return isDateRangeMatch(startDate, endDate, range)
}

/**
 * Checks if a date range matches tomorrow
 */
export const isTomorrowRange = (startDate: string, endDate: string): boolean => {
  const now = new Date()
  const tomorrow = new Date(now)
  tomorrow.setDate(now.getDate() + 1)
  const range = createDayRange(tomorrow)
  return isDateRangeMatch(startDate, endDate, range)
}

/**
 * Checks if a date range matches this week
 */
export const isThisWeekRange = (startDate: string, endDate: string): boolean => {
  const now = new Date()
  const range = createWeekRange(now)
  return isDateRangeMatch(startDate, endDate, range)
}

/**
 * Checks if a date range matches last week
 */
export const isLastWeekRange = (startDate: string, endDate: string): boolean => {
  const now = new Date()
  const range = createWeekRange(now, -1)
  return isDateRangeMatch(startDate, endDate, range)
}

/**
 * Checks if a date range matches next week
 */
export const isNextWeekRange = (startDate: string, endDate: string): boolean => {
  const now = new Date()
  const range = createWeekRange(now, 1)
  return isDateRangeMatch(startDate, endDate, range)
}

/**
 * Gets the preset name for a date range (Today, Yesterday, This Week, etc.)
 */
export const getPresetName = (startDate: string, endDate: string): string | null => {
  if (isTodayRange(startDate, endDate)) return 'Today';
  if (isYesterdayRange(startDate, endDate)) return 'Yesterday';
  if (isTomorrowRange(startDate, endDate)) return 'Tomorrow';
  if (isThisWeekRange(startDate, endDate)) return 'This Week';
  if (isLastWeekRange(startDate, endDate)) return 'Last Week';
  if (isNextWeekRange(startDate, endDate)) return 'Next Week';
  
  // Relative ranges
  const now = new Date();
  const end = parseDate(endDate);
  const start = parseDate(startDate);
  const diffTime = Math.abs(end.getTime() - start.getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  if (end.toDateString() === now.toDateString()) {
    if (diffDays === 7) return 'Last 7 Days';
    if (diffDays === 30) return 'Last 30 Days';
    if (diffDays === 90) return 'Last 90 Days';
  }
  
  return null;
}

/**
 * Checks if a duration string like "3 days" has less than 4 days
 */
export const isDurationLessThan4Days = (durationString: string): boolean => {
  if (!durationString) return false;
  const match = durationString.match(/(\d+)\s*days?/i);
  if (!match) return false;
  const days = parseInt(match[1]);
  return days < 4;
} 